"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const React = require("react");
const PathFindingLinkModel_1 = require("./PathFindingLinkModel");
const PathFindingLinkWidget_1 = require("./PathFindingLinkWidget");
const _ = require("lodash");
const Path = require("paths-js/path");
const react_diagrams_defaults_1 = require("@projectstorm/react-diagrams-defaults");
const react_canvas_core_1 = require("@projectstorm/react-canvas-core");
class PathFindingLinkFactory extends react_diagrams_defaults_1.DefaultLinkFactory {
    constructor() {
        super(PathFindingLinkFactory.NAME);
        this.ROUTING_SCALING_FACTOR = 5;
        // calculated only when smart routing is active
        this.canvasMatrix = [];
        this.routingMatrix = [];
        // used when at least one element has negative coordinates
        this.hAdjustmentFactor = 0;
        this.vAdjustmentFactor = 0;
        /**
         * Despite being a long method, we simply iterate over all three collections (nodes, ports and points)
         * to find the highest X and Y dimensions, so we can build the matrix large enough to contain all elements.
         */
        this.calculateMatrixDimensions = () => {
            const allNodesCoords = _.values(this.engine.getModel().getNodes()).map(item => ({
                x: item.getX(),
                width: item.width,
                y: item.getY(),
                height: item.height
            }));
            const allLinks = _.values(this.engine.getModel().getLinks());
            const allPortsCoords = _.flatMap(allLinks.map(link => [link.getSourcePort(), link.getTargetPort()]))
                .filter(port => port !== null)
                .map(item => ({
                x: item.getX(),
                width: item.width,
                y: item.getY(),
                height: item.height
            }));
            const allPointsCoords = _.flatMap(allLinks.map(link => link.getPoints())).map(item => ({
                // points don't have width/height, so let's just use 0
                x: item.getX(),
                width: 0,
                y: item.getY(),
                height: 0
            }));
            const sumProps = (object, props) => _.reduce(props, (acc, prop) => acc + _.get(object, prop, 0), 0);
            const canvas = this.engine.getCanvas();
            const concatedCoords = _.concat(allNodesCoords, allPortsCoords, allPointsCoords);
            const minX = Math.floor(Math.min(_.get(_.minBy(concatedCoords, 'x'), 'x', 0), 0) / this.ROUTING_SCALING_FACTOR) *
                this.ROUTING_SCALING_FACTOR;
            const maxXElement = _.maxBy(concatedCoords, item => sumProps(item, ['x', 'width']));
            const maxX = Math.max(sumProps(maxXElement, ['x', 'width']), canvas.offsetWidth);
            const minYCoords = _.minBy(concatedCoords, 'y');
            const minY = Math.floor(Math.min(_.get(minYCoords, 'y', 0), 0) / this.ROUTING_SCALING_FACTOR) * this.ROUTING_SCALING_FACTOR;
            const maxYElement = _.maxBy(concatedCoords, item => sumProps(item, ['y', 'height']));
            const maxY = Math.max(sumProps(maxYElement, ['y', 'height']), canvas.offsetHeight);
            return {
                width: Math.ceil(Math.abs(minX) + maxX),
                hAdjustmentFactor: Math.abs(minX) / this.ROUTING_SCALING_FACTOR + 1,
                height: Math.ceil(Math.abs(minY) + maxY),
                vAdjustmentFactor: Math.abs(minY) / this.ROUTING_SCALING_FACTOR + 1
            };
        };
        /**
         * Updates (by reference) where nodes will be drawn on the matrix passed in.
         */
        this.markNodes = (matrix) => {
            _.values(this.engine.getModel().getNodes()).forEach(node => {
                const startX = Math.floor(node.getX() / this.ROUTING_SCALING_FACTOR);
                const endX = Math.ceil((node.getX() + node.width) / this.ROUTING_SCALING_FACTOR);
                const startY = Math.floor(node.getY() / this.ROUTING_SCALING_FACTOR);
                const endY = Math.ceil((node.getY() + node.height) / this.ROUTING_SCALING_FACTOR);
                for (let x = startX - 1; x <= endX + 1; x++) {
                    for (let y = startY - 1; y < endY + 1; y++) {
                        this.markMatrixPoint(matrix, this.translateRoutingX(x), this.translateRoutingY(y));
                    }
                }
            });
        };
        /**
         * Updates (by reference) where ports will be drawn on the matrix passed in.
         */
        this.markPorts = (matrix) => {
            const allElements = _.flatMap(_.values(this.engine.getModel().getLinks()).map(link => [].concat(link.getSourcePort(), link.getTargetPort())));
            allElements
                .filter(port => port !== null)
                .forEach(port => {
                const startX = Math.floor(port.x / this.ROUTING_SCALING_FACTOR);
                const endX = Math.ceil((port.x + port.width) / this.ROUTING_SCALING_FACTOR);
                const startY = Math.floor(port.y / this.ROUTING_SCALING_FACTOR);
                const endY = Math.ceil((port.y + port.height) / this.ROUTING_SCALING_FACTOR);
                for (let x = startX - 1; x <= endX + 1; x++) {
                    for (let y = startY - 1; y < endY + 1; y++) {
                        this.markMatrixPoint(matrix, this.translateRoutingX(x), this.translateRoutingY(y));
                    }
                }
            });
        };
        this.markMatrixPoint = (matrix, x, y) => {
            if (matrix[y] !== undefined && matrix[y][x] !== undefined) {
                matrix[y][x] = 1;
            }
        };
    }
    setDiagramEngine(engine) {
        super.setDiagramEngine(engine);
        // listen for drag changes
        engine.getStateMachine().registerListener({
            stateChanged: event => {
                if (event.newState instanceof react_canvas_core_1.AbstractDisplacementState) {
                    const deRegister = engine.getActionEventBus().registerAction(new react_canvas_core_1.Action({
                        type: react_canvas_core_1.InputType.MOUSE_UP,
                        fire: () => {
                            this.calculateRoutingMatrix();
                            engine.repaintCanvas();
                            deRegister();
                        }
                    }));
                }
            }
        });
        this.listener = engine.registerListener({
            canvasReady: () => {
                _.defer(() => {
                    this.calculateRoutingMatrix();
                    engine.repaintCanvas();
                });
            }
        });
    }
    setFactoryBank(bank) {
        super.setFactoryBank(bank);
        if (!bank && this.listener) {
            this.listener.deregister();
        }
    }
    generateReactWidget(event) {
        return React.createElement(PathFindingLinkWidget_1.PathFindingLinkWidget, { diagramEngine: this.engine, link: event.model, factory: this });
    }
    generateModel(event) {
        return new PathFindingLinkModel_1.PathFindingLinkModel();
    }
    /**
     * A representation of the canvas in the following format:
     *
     * +-----------------+
     * | 0 0 0 0 0 0 0 0 |
     * | 0 0 0 0 0 0 0 0 |
     * | 0 0 0 0 0 0 0 0 |
     * | 0 0 0 0 0 0 0 0 |
     * | 0 0 0 0 0 0 0 0 |
     * +-----------------+
     *
     * In which all walkable points are marked by zeros.
     * It uses @link{#ROUTING_SCALING_FACTOR} to reduce the matrix dimensions and improve performance.
     */
    getCanvasMatrix() {
        if (this.canvasMatrix.length === 0) {
            this.calculateCanvasMatrix();
        }
        return this.canvasMatrix;
    }
    calculateCanvasMatrix() {
        const { width: canvasWidth, hAdjustmentFactor, height: canvasHeight, vAdjustmentFactor } = this.calculateMatrixDimensions();
        this.hAdjustmentFactor = hAdjustmentFactor;
        this.vAdjustmentFactor = vAdjustmentFactor;
        const matrixWidth = Math.ceil(canvasWidth / this.ROUTING_SCALING_FACTOR);
        const matrixHeight = Math.ceil(canvasHeight / this.ROUTING_SCALING_FACTOR);
        this.canvasMatrix = _.range(0, matrixHeight).map(() => {
            return new Array(matrixWidth).fill(0);
        });
    }
    /**
     * A representation of the canvas in the following format:
     *
     * +-----------------+
     * | 0 0 1 1 0 0 0 0 |
     * | 0 0 1 1 0 0 1 1 |
     * | 0 0 0 0 0 0 1 1 |
     * | 1 1 0 0 0 0 0 0 |
     * | 1 1 0 0 0 0 0 0 |
     * +-----------------+
     *
     * In which all points blocked by a node (and its ports) are
     * marked as 1; points were there is nothing (ie, free) receive 0.
     */
    getRoutingMatrix() {
        if (this.routingMatrix.length === 0) {
            this.calculateRoutingMatrix();
        }
        return this.routingMatrix;
    }
    calculateRoutingMatrix() {
        const matrix = _.cloneDeep(this.getCanvasMatrix());
        // nodes need to be marked as blocked points
        this.markNodes(matrix);
        // same thing for ports
        this.markPorts(matrix);
        this.routingMatrix = matrix;
    }
    /**
     * The routing matrix does not have negative indexes, but elements could be negatively positioned.
     * We use the functions below to translate back and forth between these coordinates, relying on the
     * calculated values of hAdjustmentFactor and vAdjustmentFactor.
     */
    translateRoutingX(x, reverse = false) {
        return x + this.hAdjustmentFactor * (reverse ? -1 : 1);
    }
    translateRoutingY(y, reverse = false) {
        return y + this.vAdjustmentFactor * (reverse ? -1 : 1);
    }
    generateDynamicPath(pathCoords) {
        let path = Path();
        path = path.moveto(pathCoords[0][0] * this.ROUTING_SCALING_FACTOR, pathCoords[0][1] * this.ROUTING_SCALING_FACTOR);
        pathCoords.slice(1).forEach(coords => {
            path = path.lineto(coords[0] * this.ROUTING_SCALING_FACTOR, coords[1] * this.ROUTING_SCALING_FACTOR);
        });
        return path.print();
    }
}
PathFindingLinkFactory.NAME = 'pathfinding';
exports.PathFindingLinkFactory = PathFindingLinkFactory;
//# sourceMappingURL=PathFindingLinkFactory.js.map